import {
  NavLink,
  Outlet,
  useNavigate,
  useLocation,
  Link,
} from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import {
  LayoutDashboard,
  BookOpen,
  UserRound,
  CalendarDays,
  Settings,
  LogOut,
  Stethoscope,
  ClipboardCheck,
  MessageSquareWarning,
  ClipboardList,
  Users,
  Menu,
  X,
  ChevronDown,
  Search,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePendingReviewCommentCount } from '../../hooks/usePendingReviewCommentCount';
import { useFamily } from '../../hooks/useFamily';
import { useOnboardingNavLock } from '../../hooks/useOnboardingNavLock';
import ProfileCompletionBanner from '../common/ProfileCompletionBanner';
import DoctorGlobalSearchModal from './DoctorGlobalSearchModal';
import { ProfileSwitchMenu } from '../common/ProfileSwitchMenu';
import { RoleSwitchIcon } from '../common/RoleSwitchIcon';
import { stableAvatarUrlKey } from '../../utils/avatarUrl';
import { recipeApi } from '../../api/admin/RecipeApi';
import { teamApi } from '../../api/teamApi';
import { useSocket } from '../../hooks/useSocket';
import { showInfoToast } from '../../utils/toast';
const getInitials = (name: string) =>
  name
    .split(' ')
    .map((n: string) => n[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);

type NavItem = {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
  badge?: number;
  disabled?: boolean;
  title?: string;
  /** When true, NavLink only matches exact path (avoids /team matching /team/invite). */
  end?: boolean;
};

type NavSection = { title: string; items: NavItem[] };

const DoctorLayout = () => {
  const {
    logout,
    user,
    refreshUser,
    switchProfile,
    addDoctorProfile,
    googleOAuthInProgress,
    isDoctorAccount,
    isImpersonating,
    stopImpersonation,
    impersonation,
    isLoading: isAuthLoading,
    isAuthenticated,
  } = useAuth();
  const pendingCommentCount = usePendingReviewCommentCount();
  const socket = useSocket();
  const { isNavLocked } = useOnboardingNavLock();
  const isHeaderLockedEffective = isNavLocked || !!googleOAuthInProgress;
  const lockedPointer = isHeaderLockedEffective ? 'pointer-events-none' : '';
  const navigate = useNavigate();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('isDoctorDesktopSidebarOpen');
    return saved !== null ? saved === 'true' : true;
  });

  useEffect(() => {
    localStorage.setItem(
      'isDoctorDesktopSidebarOpen',
      String(isDesktopSidebarOpen),
    );
  }, [isDesktopSidebarOpen]);

  // Listen for custom sidebar events (e.g., auto-collapse when AI Review Drawer opens)
  useEffect(() => {
    const handleSidebarEvent = (e: Event) => {
      const ce = e as CustomEvent<{ open?: boolean }>;
      if (typeof ce.detail?.open === 'boolean') {
        setIsDesktopSidebarOpen(ce.detail.open);
        if (window.innerWidth < 1024) {
          setIsMobileSidebarOpen(ce.detail.open);
        }
      }
    };
    window.addEventListener('meraayurved:doctor-sidebar', handleSidebarEvent);
    return () => {
      window.removeEventListener(
        'meraayurved:doctor-sidebar',
        handleSidebarEvent,
      );
    };
  }, []);

  // Global search: Ctrl/Cmd+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsGlobalSearchOpen(true);
        setIsMobileSidebarOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isSwitchRoleDropdownOpen, setIsSwitchRoleDropdownOpen] =
    useState(false);
  const [doctorReviewQueueCount, setDoctorReviewQueueCount] = useState(0);
  const [teamInviteCount, setTeamInviteCount] = useState(0);
  const [teamInviteBadgeDismissed, setTeamInviteBadgeDismissed] =
    useState(false);
  const [profileSwitchBusy, setProfileSwitchBusy] = useState(false);
  const location = useLocation();
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const switchRoleDropdownRef = useRef<HTMLDivElement>(null);
  const prevTeamInviteCountRef = useRef<number | null>(null);
  const { familyMembers, activeUserId } = useFamily();

  useEffect(() => {
    if (!isHeaderLockedEffective) return;
    queueMicrotask(() => {
      setIsSwitchRoleDropdownOpen(false);
    });
  }, [isHeaderLockedEffective]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonation = async () => {
    await stopImpersonation();
    navigate('/admin/dashboard');
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsMobileSidebarOpen(false), 0);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (profileMenuRef.current && !profileMenuRef.current.contains(t)) {
        setIsProfileMenuOpen(false);
      }
      if (
        switchRoleDropdownRef.current &&
        !switchRoleDropdownRef.current.contains(t)
      ) {
        setIsSwitchRoleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const intervalRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Poll only for doctor **accounts** in a **provider** session (not while acting as patient on member site).
    if (
      isAuthLoading ||
      !isAuthenticated ||
      !user ||
      !isDoctorAccount() ||
      user.active_profile_type === 'patient' ||
      isImpersonating
    ) {
      return;
    }

    let cancelled = false;

    const doRefresh = async () => {
      if (cancelled) return;
      await refreshUser();
    };

    // Do not refresh on mount — it races AuthRedirect / getProfile and can flip
    // active_profile_type between responses, causing redirect loops. Interval + focus
    // are enough to pick up server changes.

    const onFocus = () => doRefresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') doRefresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    if (!intervalRef.current) {
      intervalRef.current = window.setInterval(() => {
        void doRefresh();
      }, 15000);
    }

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [
    refreshUser,
    user,
    isDoctorAccount,
    isAuthLoading,
    isAuthenticated,
    isImpersonating,
  ]);

  useEffect(() => {
    if (user?.doctorProfile?.status === 'approved' && intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  }, [user?.doctorProfile?.status]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  }, []);

  const toggleSidebar = () => {
    if (window.innerWidth >= 1024) {
      setIsDesktopSidebarOpen(!isDesktopSidebarOpen);
    } else {
      setIsMobileSidebarOpen(!isMobileSidebarOpen);
    }
  };

  const isDoctorApproved = user?.doctorProfile?.status === 'approved';
  const doctorReviewBadgeCount = isDoctorApproved ? doctorReviewQueueCount : 0;

  useEffect(() => {
    if (isAuthLoading || !isAuthenticated || !isDoctorApproved) {
      return;
    }

    // Badge = remedies still waiting for THIS doctor's review (not already reviewed)
    const loadDoctorReviewQueueCount = async () => {
      const response = await recipeApi.getDoctorReviewQueue({
        page: 1,
        limit: 1,
        review_status: 'pending',
      });

      if (response.success && response.data) {
        const queueData = response.data as {
          pagination?: { total?: number };
          total?: number;
        };
        const total =
          queueData.pagination?.total ??
          (typeof queueData.total === 'number' ? queueData.total : 0);
        setDoctorReviewQueueCount(total);
      }
    };

    void loadDoctorReviewQueueCount();
    window.addEventListener('focus', loadDoctorReviewQueueCount);
    window.addEventListener(
      'meraayurved:doctor-review-queue-changed',
      loadDoctorReviewQueueCount,
    );
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void loadDoctorReviewQueueCount();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('focus', loadDoctorReviewQueueCount);
      window.removeEventListener(
        'meraayurved:doctor-review-queue-changed',
        loadDoctorReviewQueueCount,
      );
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isDoctorApproved, isAuthLoading, isAuthenticated, user?.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsGlobalSearchOpen(true);
        setIsMobileSidebarOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (isAuthLoading || !isAuthenticated) {
      return;
    }

    const loadTeamInviteCount = async () => {
      const [incomingRes, outgoingRes] = await Promise.all([
        teamApi.getIncomingInvites(),
        teamApi.getOutgoingInvites(),
      ]);

      const incomingCount =
        incomingRes.success && incomingRes.data
          ? incomingRes.data.invitations.length
          : 0;
      // Author-side badge should reflect response updates, not "sent pending" invites.
      const outgoingResponseCount =
        outgoingRes.success && outgoingRes.data
          ? outgoingRes.data.invitations.filter((i) => i.status === 'declined')
              .length
          : 0;

      setTeamInviteCount(incomingCount + outgoingResponseCount);
    };

    void loadTeamInviteCount();
    window.addEventListener('focus', loadTeamInviteCount);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void loadTeamInviteCount();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener(
      'meraayurved:team-invites-changed',
      loadTeamInviteCount,
    );
    window.addEventListener(
      'meraayurved:identity-changed',
      loadTeamInviteCount,
    );
    const intervalId = window.setInterval(() => {
      void loadTeamInviteCount();
    }, 20000);

    return () => {
      window.removeEventListener('focus', loadTeamInviteCount);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(
        'meraayurved:team-invites-changed',
        loadTeamInviteCount,
      );
      window.removeEventListener(
        'meraayurved:identity-changed',
        loadTeamInviteCount,
      );
      window.clearInterval(intervalId);
    };
  }, [isAuthLoading, isAuthenticated, user?.id]);

  // Websocket: badge/toast only — never mutate queue/table pages from this.
  useEffect(() => {
    if (!socket || isAuthLoading || !isAuthenticated) return;

    const refreshDoctorReviewBadge = async () => {
      if (user?.doctorProfile?.status !== 'approved') return;
      const response = await recipeApi.getDoctorReviewQueue({
        page: 1,
        limit: 1,
        review_status: 'pending',
      });
      if (response.success && response.data) {
        const queueData = response.data as {
          pagination?: { total?: number };
          total?: number;
        };
        const total =
          queueData.pagination?.total ??
          (typeof queueData.total === 'number' ? queueData.total : 0);
        setDoctorReviewQueueCount(total);
      }
    };

    const refreshTeamInviteBadge = async () => {
      const [incomingRes, outgoingRes] = await Promise.all([
        teamApi.getIncomingInvites(),
        teamApi.getOutgoingInvites(),
      ]);
      const incomingCount =
        incomingRes.success && incomingRes.data
          ? incomingRes.data.invitations.length
          : 0;
      const outgoingResponseCount =
        outgoingRes.success && outgoingRes.data
          ? outgoingRes.data.invitations.filter((i) => i.status === 'declined')
              .length
          : 0;
      setTeamInviteCount(incomingCount + outgoingResponseCount);
    };

    const onAppNotification = (payload: {
      kind?: string;
      message?: string;
      data?: { actorUserId?: number | string };
    }) => {
      const kind = payload?.kind;
      if (kind === 'doctor_review_queue') {
        void refreshDoctorReviewBadge();
        window.dispatchEvent(
          new Event('meraayurved:doctor-review-queue-changed'),
        );
      }
      if (kind === 'team_invites') {
        void refreshTeamInviteBadge();
        // SocketProvider also dispatches; keep local badge refresh here.
      }
      if (kind === 'doctor_profile_status') {
        void refreshUser?.();
      }
      // recipe_status / recipe_team_access: SocketProvider dispatches list events.
      // Skip toast when this user was the actor (local success toast already shown),
      // and skip recipe_team_access here — DoctorRecipe / wizard show a single toast.
      const actorId = payload?.data?.actorUserId;
      const isOwnAction =
        actorId != null &&
        user?.id != null &&
        String(actorId) === String(user.id);
      const canApprove = Boolean(user?.can_approve || user?.canApprove);
      if (
        payload?.message &&
        !isOwnAction &&
        kind !== 'recipe_team_access' &&
        !(kind === 'recipe_queue' && !canApprove)
      ) {
        showInfoToast(payload.message);
      }
    };

    socket.on('app_notification', onAppNotification);
    return () => {
      socket.off('app_notification', onAppNotification);
    };
  }, [
    socket,
    isAuthLoading,
    isAuthenticated,
    user?.id,
    user?.can_approve,
    user?.canApprove,
    user?.doctorProfile?.status,
    refreshUser,
  ]);

  useEffect(() => {
    if (location.pathname.startsWith('/doctor/team')) {
      // Defer to satisfy react-hooks/set-state-in-effect lint.
      queueMicrotask(() => setTeamInviteBadgeDismissed(true));
    }
  }, [location.pathname]);

  useEffect(() => {
    const prev = prevTeamInviteCountRef.current;
    if (prev !== null && teamInviteCount > prev) {
      // Defer to satisfy react-hooks/set-state-in-effect lint.
      queueMicrotask(() => setTeamInviteBadgeDismissed(false));
    }
    prevTeamInviteCountRef.current = teamInviteCount;
  }, [teamInviteCount]);

  const effectiveTeamInviteBadgeCount =
    teamInviteCount > 0 && !teamInviteBadgeDismissed ? teamInviteCount : 0;

  const navSections: NavSection[] = [
    {
      title: 'Manage remedies',
      items: [
        {
          path: '/doctor/dashboard',
          label: 'Dashboard',
          icon: LayoutDashboard,
        },
        { path: '/doctor/remedy', label: 'My remedies', icon: BookOpen },
        {
          path: '/doctor/team',
          label: 'My team',
          icon: Users,
          badge: effectiveTeamInviteBadgeCount,
          end: true,
          disabled: !isDoctorApproved,
          title: 'Your profile must be approved by admin to access My team.',
        },
        {
          path: '/doctor/review-queue',
          label: 'Review remedies',
          icon: ClipboardList,
          badge: doctorReviewBadgeCount,
          disabled: !isDoctorApproved,
          title: 'Your profile must be approved by admin to review remedies.',
        },
        ...(user?.can_moderate_comments || user?.canModerateComments
          ? [
              {
                path: '/doctor/review-comments',
                label: 'Review comments',
                icon: MessageSquareWarning,
                badge: pendingCommentCount,
              },
            ]
          : []),
      ],
    },
    {
      title: 'Appointments',
      items: [
        {
          path: '/doctor/appointment',
          label: 'Appointments',
          icon: CalendarDays,
        },
      ],
    },
    {
      title: 'Account',
      items: [
        { path: '/doctor/profile', label: 'My profile', icon: UserRound },
        { path: '/doctor/setting', label: 'Settings', icon: Settings },
      ],
    },
  ];

  const displayName = user?.name || user?.email || 'Provider';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const activeMember = familyMembers.find((m) => m.id === activeUserId);
  const activeMemberName = activeMember?.name || user?.name || 'Profile';
  const activeMemberAvatar =
    activeMember?.avatar ||
    (String(activeMember?.id) === String(user?.id)
      ? (user?.avatar as string | undefined)
      : null) ||
    null;

  const [headerAvatarUrl, setHeaderAvatarUrl] = useState<string | null>(null);
  const prevHeaderAvatarRef = useRef<string | null>(null);

  useEffect(() => {
    const newUrl = activeMemberAvatar || (user?.avatar as string) || null;
    const newKey = stableAvatarUrlKey(newUrl);
    const prevKey = stableAvatarUrlKey(prevHeaderAvatarRef.current);

    if (newKey === prevKey) {
      if (newUrl && newUrl !== prevHeaderAvatarRef.current) {
        prevHeaderAvatarRef.current = newUrl;
      }
      return;
    }

    prevHeaderAvatarRef.current = newUrl;

    if (!newUrl) {
      queueMicrotask(() => setHeaderAvatarUrl(null));
      return;
    }

    const img = new Image();
    img.onload = () => setHeaderAvatarUrl(newUrl);
    img.onerror = () => setHeaderAvatarUrl(null);
    img.src = newUrl;
  }, [activeMemberAvatar, user?.avatar]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setIsMobileSidebarOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 shadow-xl lg:shadow-none transform transition-transform duration-300 ease-in-out 
          ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          ${isDesktopSidebarOpen ? 'lg:translate-x-0' : 'lg:-translate-x-full'}
          font-body h-full`}
      >
        <div className="flex flex-col h-full bg-white">
          <div className="p-5 border-b border-gray-200 flex items-start justify-between gap-2">
            <Link
              to="/doctor/dashboard"
              onClick={() => setIsMobileSidebarOpen(false)}
              className="flex items-center gap-3 min-w-0 group"
              title="Go to provider dashboard"
            >
              <div className="w-10 h-10 rounded-full bg-linear-to-br from-herbal-green to-herbal-green-dark flex items-center justify-center text-white text-sm font-semibold shadow-md shrink-0 ring-2 ring-white group-hover:ring-herbal-green/30 transition-shadow">
                {initials || <Stethoscope className="w-5 h-5" />}
              </div>
              <div className="min-w-0 text-left">
                <p className="text-sm font-bold text-gray-900 font-heading leading-tight">
                  MeraAyurved
                </p>

                <p className="text-xs text-gray-500 mt-1 truncate">
                  {displayName}
                </p>
              </div>
            </Link>
            <button
              type="button"
              onClick={() => setIsMobileSidebarOpen(false)}
              className="lg:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-lg shrink-0"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-3 pt-4 pb-1">
            <button
              type="button"
              onClick={() => {
                setIsGlobalSearchOpen(true);
                setIsMobileSidebarOpen(false);
              }}
              className="flex items-center gap-3 w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-500 hover:text-herbal-green transition-colors font-body text-sm"
            >
              <Search className="w-4 h-4 shrink-0" />
              <span className="font-medium truncate flex-1 text-left">
                Search...
              </span>
              <kbd className="hidden sm:inline text-[10px] px-1.5 py-0.5 rounded border border-gray-200 bg-white text-gray-400">
                ⌘K
              </kbd>
            </button>
          </div>

          <nav className="flex-1 px-3 pb-4 pt-2 space-y-4 overflow-y-auto">
            {navSections.map((section) => (
              <div key={section.title}>
                <p className="px-3 mb-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  {section.title}
                </p>
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const badge = item.badge;
                    if (item.disabled) {
                      return (
                        <div
                          key={item.path}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg font-body text-gray-400 cursor-not-allowed opacity-60 select-none text-sm"
                          title={item.title}
                          aria-disabled
                        >
                          <Icon className="w-5 h-5 shrink-0" />
                          <span className="font-medium">{item.label}</span>
                        </div>
                      );
                    }
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.end}
                        onClick={() => setIsMobileSidebarOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors font-body text-sm ${
                            isActive
                              ? 'bg-herbal-green text-white shadow-sm'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`
                        }
                      >
                        <Icon className="w-5 h-5 shrink-0" />
                        <span className="font-medium">{item.label}</span>
                        {badge != null && badge > 0 && (
                          <span className="ml-auto min-w-5 h-5 px-1.5 flex items-center justify-center rounded-full text-xs font-semibold bg-amber-500 text-white">
                            {badge > 99 ? '99+' : badge}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="p-3 border-t border-gray-200 bg-white">
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-body text-sm"
            >
              <LogOut className="w-5 h-5 shrink-0" />
              <span className="font-medium">Sign out</span>
            </button>
          </div>
        </div>
      </aside>

      <main
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
          isDesktopSidebarOpen ? 'lg:ml-64' : 'lg:ml-0'
        }`}
      >
        {isImpersonating && (
          <div className="bg-amber-100 border-b border-amber-200 px-4 py-2 text-amber-900 flex items-center justify-between gap-3">
            <p className="text-sm font-medium">
              Impersonating as {user?.name || user?.email || 'selected user'}.
              Session: {impersonation?.impSessionId || 'n/a'}.
            </p>
            <button
              type="button"
              onClick={handleStopImpersonation}
              className="px-3 py-1.5 rounded-md bg-amber-900 text-amber-50 text-sm font-medium hover:bg-amber-800 transition-colors"
            >
              Stop impersonation
            </button>
          </div>
        )}
        <ProfileCompletionBanner />
        <header
          className={`bg-white border-b border-gray-200 px-3 sm:px-4 py-2.5 flex items-center gap-3 sticky top-0 z-30 shadow-sm ${
            isHeaderLockedEffective
              ? 'cursor-not-allowed onboarding-nav-locked'
              : ''
          }`}
        >
          <button
            type="button"
            onClick={toggleSidebar}
            className="p-2 -ml-1 text-gray-600 hover:bg-gray-100 rounded-lg shrink-0"
            aria-label={
              isDesktopSidebarOpen ? 'Collapse sidebar' : 'Open sidebar'
            }
          >
            <Menu className="w-6 h-6" />
          </button>

          <Link
            to="/doctor/dashboard"
            className="hidden sm:block font-semibold text-gray-900 font-heading truncate shrink-0 hover:text-herbal-green transition-colors"
          >
            Provider dashboard
          </Link>

          <div className="flex items-center gap-0.5 sm:gap-1 ml-auto shrink-0">
            <div className="relative z-60" ref={switchRoleDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setIsSwitchRoleDropdownOpen((o) => !o);
                  setIsProfileMenuOpen(false);
                }}
                className={`flex flex-col items-center justify-center p-1.5 text-slate-700 hover:text-herbal-green hover:bg-herbal-green/5 rounded-lg transition-all duration-200 ${lockedPointer}`}
                aria-expanded={isSwitchRoleDropdownOpen}
                aria-haspopup="true"
                aria-label="Switch role (currently Healthcare Provider)"
                title="Switch role (currently Healthcare Provider)"
              >
                <RoleSwitchIcon role="doctor" className="w-5 h-5" />
              </button>
              {isSwitchRoleDropdownOpen && (
                <div
                  className="absolute right-0 mt-2 w-70 bg-white rounded-xl shadow-lg border border-gray-100 py-2 origin-top-right overflow-hidden z-50"
                  style={{
                    animation: 'profileDropdownIn 0.2s ease-out both',
                  }}
                >
                  <ProfileSwitchMenu
                    user={user}
                    profileSwitchBusy={profileSwitchBusy}
                    setProfileSwitchBusy={setProfileSwitchBusy}
                    setOpen={setIsSwitchRoleDropdownOpen}
                    switchProfile={switchProfile}
                    onBecomeDoctor={addDoctorProfile}
                    disableInteractions={isHeaderLockedEffective}
                  />
                </div>
              )}
            </div>

            <div className="relative z-60" ref={profileMenuRef}>
              <button
                type="button"
                data-onboarding-nav-escape
                onClick={() => {
                  setIsProfileMenuOpen((o) => !o);
                  setIsSwitchRoleDropdownOpen(false);
                }}
                className="flex items-center gap-1 rounded-full p-0.5 hover:ring-2 hover:ring-herbal-green/30 transition-all duration-300 ease-out"
                aria-expanded={isProfileMenuOpen}
                aria-haspopup="true"
                aria-label="Profile menu"
              >
                <div className="rounded-full shrink-0">
                  {headerAvatarUrl ? (
                    <img
                      src={headerAvatarUrl}
                      alt={activeMemberName}
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-herbal-green/25 ring-offset-1"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-herbal-green flex items-center justify-center ring-2 ring-herbal-green/20 ring-offset-1">
                      <span className="text-white text-xs font-semibold">
                        {getInitials(activeMemberName)}
                      </span>
                    </div>
                  )}
                </div>
                <ChevronDown
                  className={`w-3.5 h-3.5 text-gray-500 hidden sm:block transition-transform duration-300 ease-out ${isProfileMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isProfileMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden origin-top-right"
                  style={{
                    animation:
                      'profileDropdownIn 0.26s cubic-bezier(0.22, 1, 0.36, 1) both',
                  }}
                >
                  <div
                    className={
                      isHeaderLockedEffective
                        ? 'pointer-events-none select-none opacity-60'
                        : ''
                    }
                    aria-hidden={isHeaderLockedEffective ? true : undefined}
                  >
                    {(user?.can_approve || user?.canApprove) && (
                      <div className="border-t border-gray-100 pt-3 pb-1">
                        <button
                          type="button"
                          onClick={() => {
                            setIsProfileMenuOpen(false);
                            navigate('/doctor/approver-queue?tab=reviewed');
                          }}
                          className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <ClipboardCheck className="w-4 h-4 text-gray-400 shrink-0" />
                          My Reviewed
                        </button>
                      </div>
                    )}
                    {(user?.can_moderate_comments ||
                      user?.canModerateComments) && (
                      <div className="border-t border-gray-100 pt-1 pb-1">
                        <button
                          type="button"
                          onClick={() => {
                            setIsProfileMenuOpen(false);
                            navigate('/doctor/review-comments');
                          }}
                          className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <MessageSquareWarning className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="flex-1 text-left">
                            Review comments
                          </span>
                          {pendingCommentCount > 0 && (
                            <span className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none inline-flex items-center justify-center">
                              {pendingCommentCount > 99
                                ? '99+'
                                : pendingCommentCount}
                            </span>
                          )}
                        </button>
                      </div>
                    )}

                    <div className="border-t border-gray-100 pt-1 pb-1">
                      <button
                        type="button"
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          navigate('/doctor/profile');
                        }}
                        className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <UserRound className="w-4 h-4 text-gray-400 shrink-0" />
                        Provider profile
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsProfileMenuOpen(false);
                          navigate('/doctor/setting');
                        }}
                        className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Settings className="w-4 h-4 text-gray-400 shrink-0" />
                        Settings
                      </button>
                    </div>
                  </div>

                  <div
                    className={`border-t border-gray-100 ${isHeaderLockedEffective ? 'pointer-events-auto relative z-1' : ''}`}
                  >
                    {isImpersonating && (
                      <button
                        type="button"
                        onClick={async () => {
                          setIsProfileMenuOpen(false);
                          await handleStopImpersonation();
                        }}
                        className="w-full px-3 py-3 text-left text-sm text-amber-700 hover:bg-amber-50 transition-colors font-medium"
                      >
                        Stop impersonation
                      </button>
                    )}
                    <button
                      type="button"
                      data-onboarding-nav-escape
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full px-3 py-3 text-left text-sm text-gray-500 hover:bg-gray-50 transition-colors"
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8 max-w-full overflow-x-hidden flex-1">
          <Outlet />
        </div>
      </main>

      <DoctorGlobalSearchModal
        open={isGlobalSearchOpen}
        onClose={() => setIsGlobalSearchOpen(false)}
      />
    </div>
  );
};

export default DoctorLayout;
